import { createSlice } from "@reduxjs/toolkit";
import { BadgeSyncStatus, ColocatedShowDataSyncStatus, PAPER_SIZE } from "../../../common/enums";
import { RootState } from "../../types";
import dayjs from "../../../common/dayjs";

const initialState: RootState["metadata"] = {
  isAdmin: false,
  lastBadgeUpdateTime: dayjs(new Date()).format(),
  badgePrintFormat: PAPER_SIZE.FOUR_X_TEN_INCH,
  offline: false,
  badgeSyncStatus: BadgeSyncStatus.DOWNLOADING,
  activeBadgeSyncRequests: 0,
  colocatedShowDataSyncVisible: false,
  colocatedShowDataSyncStatus: ColocatedShowDataSyncStatus.IDLE,
  colocatedShowDataSyncProgress: 0,
  fontsLoaded: false
};

const metadataSlice = createSlice({
  name: "metadata",
  initialState,
  reducers: {
    setBadgePrintFormat: (state, action) => {
      state.badgePrintFormat = action.payload;
    },
    setLastBadgeUpdateTime: (state, action) => {
      state.lastBadgeUpdateTime = action.payload;
    },
    setOffline: (state, action) => {
      state.offline = action.payload;
    },
    setIsAdmin: (state, action) => {
      state.isAdmin = action.payload;
    },

    badgeSyncRequestStarted: state => {
      state.activeBadgeSyncRequests = (state.activeBadgeSyncRequests ?? 0) + 1;
      state.badgeSyncStatus = BadgeSyncStatus.DOWNLOADING;
    },
    badgeSyncRequestCompleted: state => {
      const current = state.activeBadgeSyncRequests ?? 0;
      state.activeBadgeSyncRequests = current > 0 ? current - 1 : 0;

      if (
        state.activeBadgeSyncRequests === 0 &&
        state.badgeSyncStatus !== BadgeSyncStatus.DOWNLOAD_FAILED &&
        state.badgeSyncStatus !== BadgeSyncStatus.SYNCING_FAILED
      ) {
        state.badgeSyncStatus = BadgeSyncStatus.SUCCESS;
      }
    },
    badgeSyncRequestFailed: state => {
      const current = state.activeBadgeSyncRequests ?? 0;
      state.activeBadgeSyncRequests = current > 0 ? current - 1 : 0;
      state.badgeSyncStatus = BadgeSyncStatus.DOWNLOAD_FAILED;
    },
    badgeSyncSyncFailed: state => {
      state.activeBadgeSyncRequests = 0;
      state.badgeSyncStatus = BadgeSyncStatus.SYNCING_FAILED;
    },
    colocatedShowDataSyncStarted: state => {
      state.colocatedShowDataSyncVisible = true;
      state.colocatedShowDataSyncStatus = ColocatedShowDataSyncStatus.SYNCING;
      state.colocatedShowDataSyncProgress = 0;
    },
    colocatedShowDataSyncProgressUpdated: (state, action) => {
      const value = Number(action.payload ?? 0);
      state.colocatedShowDataSyncProgress = Math.max(0, Math.min(100, value));
    },
    colocatedShowDataSyncFailed: state => {
      state.colocatedShowDataSyncVisible = true;
      state.colocatedShowDataSyncStatus = ColocatedShowDataSyncStatus.FAILED;
    },
    colocatedShowDataSyncCompleted: state => {
      state.colocatedShowDataSyncVisible = false;
      state.colocatedShowDataSyncStatus = ColocatedShowDataSyncStatus.IDLE;
      state.colocatedShowDataSyncProgress = 100;
    },
    resetColocatedShowDataSyncState: state => {
      state.colocatedShowDataSyncVisible = false;
      state.colocatedShowDataSyncStatus = ColocatedShowDataSyncStatus.IDLE;
      state.colocatedShowDataSyncProgress = 0;
    },
    setFontsLoaded: (state, action) => {
      state.fontsLoaded = action.payload;
    }
  }
});

export const {
  setBadgePrintFormat,
  setLastBadgeUpdateTime,
  setOffline,
  setIsAdmin,
  setFontsLoaded,
  badgeSyncRequestStarted,
  badgeSyncRequestCompleted,
  badgeSyncRequestFailed,
  badgeSyncSyncFailed,
  colocatedShowDataSyncStarted,
  colocatedShowDataSyncProgressUpdated,
  colocatedShowDataSyncFailed,
  colocatedShowDataSyncCompleted,
  resetColocatedShowDataSyncState
} = metadataSlice.actions;

export default metadataSlice.reducer;
